import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Fade,
  Backdrop,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClientSelectionDialog from "./ClientSelectionDialog";
import ProductSelectionDialog from "./ProductSelectionDialog";
import { AddProduitModal } from "./ProduitModals";
import {
  fetchBrands,
  fetchCategories,
  fetchBsgCode,
} from "./global/apiService";
import api from "./global/apiConfig";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  width: { xs: "95%", sm: "95%", md: "95%" },
  height: "auto",
  maxHeight: "95vh",
  maxWidth: "95vw",
  overflowY: "auto",
};

export const AddVenteModal = ({
  open,
  handleClose,
  handleSubmit,
  clients = [],
  products = [],
  onProductsUpdated,
}) => {
  // State for dialogs
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);

  const [saleData, setSaleData] = useState({
    id_client: "",
    date_vente: new Date().toISOString().split("T")[0],
    type_paiement: "",
    statut_paiement: "En attente",
    commentaire: "",
    montant_total: 0,
    details: [],
  });

  const handleProductAdded = async () => {
    if (onProductsUpdated) {
      await onProductsUpdated();
    }
  };

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState({
    id_produit: "",
    quantite: "",
    prix_vente_unitaire: "",
    montant_total: 0,
  });

  const [stockWarning, setStockWarning] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState(null);
  const refreshProducts = async () => {
    try {
      const response = await api.get("produits.php");
      // Update the products state
      if (response.data) {
        // Pass the updated products back to parent component
        if (onProductsUpdated) {
          onProductsUpdated(response.data);
        }
      }
    } catch (error) {
      console.error("Error refreshing products:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesData, brandsData] = await Promise.all([
          fetchCategories(),
          fetchBrands(),
        ]);
        setCategories(categoriesData);
        setBrands(brandsData);
      } catch (error) {
        console.error("Error loading data:", error);
        setError("Error loading necessary data");
      }
    };
    loadData();
  }, []);

  const calculateTotal = (details) => {
    const total = details.reduce((sum, detail) => {
      const detailTotal = Number(detail.montant_total) || 0;
      return sum + detailTotal;
    }, 0);

    return total.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: "currency",
      currency: "MAD",
    });
  };
  const handleOpenClientDialog = () => setClientDialogOpen(true);
  const handleCloseClientDialog = () => setClientDialogOpen(false);
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSaleData((prev) => ({
      ...prev,
      id_client: client.id_client,
    }));
    setClientDialogOpen(false);
  };

  // Handlers for product selection
  const handleOpenProductDialog = () => setProductDialogOpen(true);
  const handleCloseProductDialog = () => setProductDialogOpen(false);
  const handleProductSelect = (product) => {
    setSelectedProduct({
      id_produit: product.id_produit,
      quantite: 1,
      prix_vente_unitaire: product.prix_vente,
      montant_total: product.prix_vente,
    });
    setProductDialogOpen(false);
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSaleData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setSelectedProduct((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      if (name === "quantite" || name === "prix_vente_unitaire") {
        updated.montant_total = updated.quantite * updated.prix_vente_unitaire;
      }
      return updated;
    });
  };

  // Add product to sale
  const addProductToSale = () => {
    if (!selectedProduct.id_produit || !selectedProduct.quantite) return;

    const product = products.find(
      (p) => p.id_produit === selectedProduct.id_produit
    );
    if (!product) return;

    // Convert strings to numbers and calculate the total
    const quantity = Number(selectedProduct.quantite) || 0;
    const price = Number(selectedProduct.prix_vente_unitaire) || 0;
    const detailTotal = quantity * price;

    setSaleData((prev) => ({
      ...prev,
      details: [
        ...prev.details,
        {
          ...selectedProduct,
          montant_total: detailTotal,
        },
      ],
    }));

    setSelectedProduct({
      id_produit: "",
      quantite: "",
      prix_vente_unitaire: "",
      montant_total: 0,
    });
    setStockWarning(null);
  };

  // Remove product from sale
  const removeProduct = (index) => {
    setSaleData((prev) => {
      const newDetails = prev.details.filter((_, i) => i !== index);
      return {
        ...prev,
        details: newDetails,
        montant_total: newDetails.reduce(
          (sum, item) => sum + item.montant_total,
          0
        ),
      };
    });
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (
      !saleData.id_client ||
      !saleData.type_paiement ||
      saleData.details.length === 0
    ) {
      return;
    }
    handleSubmit(saleData);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      aria-labelledby="add-sale-modal-title"
    >
      <Fade in={open}>
        <Box sx={modalStyle} role="dialog" aria-modal="true">
          <Typography
            id="add-sale-modal-title"
            variant="h5"
            sx={{ mb: 3, color: "primary.main" }}
          >
            Nouvelle Vente
          </Typography>

          <form onSubmit={handleFormSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <TextField
                    label="Client"
                    value={
                      selectedClient
                        ? `${selectedClient.nom} ${selectedClient.prenom}`
                        : ""
                    }
                    onClick={handleOpenClientDialog}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de vente"
                  type="date"
                  name="date_vente"
                  value={saleData.date_vente}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type de paiement</InputLabel>
                  <Select
                    name="type_paiement"
                    value={saleData.type_paiement}
                    onChange={handleChange}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Tranche">Tranche</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Statut du paiement</InputLabel>
                  <Select
                    name="statut_paiement"
                    value={saleData.statut_paiement}
                    onChange={handleChange}
                  >
                    <MenuItem value="Payé">Payé</MenuItem>
                    <MenuItem value="En attente">En attente</MenuItem>
                    <MenuItem value="Partiel">Partiel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                  Ajouter des produits
                </Typography>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Produit"
                      value={
                        selectedProduct.id_produit
                          ? products.find(
                              (p) => p.id_produit === selectedProduct.id_produit
                            )?.designation
                          : ""
                      }
                      onClick={handleOpenProductDialog}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Quantité"
                      type="number"
                      name="quantite"
                      value={selectedProduct.quantite}
                      onChange={handleProductChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Prix unitaire"
                      type="number"
                      name="prix_vente_unitaire"
                      value={selectedProduct.prix_vente_unitaire}
                      onChange={handleProductChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={addProductToSale}
                      startIcon={<AddIcon />}
                    >
                      Ajouter
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Produit</TableCell>
                        <TableCell align="right">Quantité</TableCell>
                        <TableCell align="right">Prix unitaire</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {saleData.details.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {
                              products.find(
                                (p) => p.id_produit === detail.id_produit
                              )?.designation
                            }
                          </TableCell>
                          <TableCell align="right">{detail.quantite}</TableCell>
                          <TableCell align="right">
                            {detail.prix_vente_unitaire} MAD
                          </TableCell>
                          <TableCell align="right">
                            {detail.montant_total} MAD
                          </TableCell>
                          <TableCell align="right">
                            <IconButton onClick={() => removeProduct(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{calculateTotal(saleData.details)}</strong>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Commentaire"
                  name="commentaire"
                  value={saleData.commentaire}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 4,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button variant="outlined" onClick={handleClose}>
                Annuler
              </Button>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                disabled={saleData.details.length === 0}
              >
                Enregistrer
              </Button>
            </Box>
          </form>

          <ClientSelectionDialog
            open={clientDialogOpen}
            onClose={handleCloseClientDialog}
            onSelect={handleClientSelect}
            clients={clients}
          />

          <ProductSelectionDialog
            open={productDialogOpen}
            onClose={handleCloseProductDialog}
            onSelect={handleProductSelect}
            categories={categories}
            brands={brands}
            products={products}
            onProductAdded={handleProductAdded}
          />
        </Box>
      </Fade>
    </Modal>
  );
};

export default { AddVenteModal };
